// utils/saveAsDraft.js
//
// Save as Draft in the JD editor (Save as Draft design v4.0, sections 4, 7, 12 and 13; wireframe
// jobs-library-ownership-v3.html: the editor's buttons, the Cancel popup and the draft toasts).
// Pure helpers, so the button rules, the Cancel popup, the toasts and the stale-tab values are tested
// without rendering the page. The server makes the final call on every rule (it refuses STALE, IN_REVIEW,
// NOT_OWNER and NO_CHANGES); these only decide what the buttons offer.

import { JD_STATUSES, hasManualEdits } from 'constants/jobs.constants';

// ── Wording (wireframe) ───────────────────────────────────────────────────────
export const SAVE_AS_DRAFT_LABEL = 'Save as Draft';
export const TOOLTIP_SAVE_AS_DRAFT = 'Save privately. Only you and the people with access (your delegate, and anyone it is shared with) can see this draft until you send it for approval or publish it.';
// Every editor button that would store a version identical to the one before it (design, Button rules)
export const TOOLTIP_MAKE_CHANGES_FIRST = 'Make changes first';
export const DRAFT_SAVED_TOAST = 'Draft saved. Only you and the people with access can see it.';
export const DRAFT_UPDATED_TOAST = 'Draft updated. Only you and the people with access can see it.';

// The Cancel popup, shown only when something changed (Close / Esc does nothing: the user picks a button)
export const DRAFT_CANCEL_CONFIRM = {
  title: 'Are you sure?',
  body: 'You will lose your progress. Save as draft to keep the changes.',
  discardLabel: 'Discard changes',
  keepEditingLabel: 'Keep editing',
  saveDraftLabel: SAVE_AS_DRAFT_LABEL,
};

// ── Content: "changed" is worked out from the text, never from click history ──
// Whitespace-insensitive, the same rule as the server's NO_CHANGES check (normalize_md).
export const normalizeContent = (text) => (typeof text === 'string' ? text : '').replace(/\s+/g, ' ').trim();
export const isSameContent = (a, b) => normalizeContent(a) === normalizeContent(b);

// Is the version the editor opened the owner's human-edited draft? (Saving it again overwrites it.)
export const isHumanEditedDraft = (jdStatus, jdInputType) =>
  jdStatus === JD_STATUSES.DRAFT && hasManualEdits(jdInputType);

// The version the editor's result would be stored on top of: for a human-edited draft (overwritten in
// place) the version it grew from, its parent; otherwise the loaded version itself. Its content is
// fetched only in the first case (previousContent below).
export const getPreviousStoredVersionId = (latestRow, isHumanDraft) =>
  (isHumanDraft ? latestRow?.parent_sb_unique_id : latestRow?.sb_unique_id) || null;

/**
 * The editor's button rules. No two consecutive stored versions can be identical:
 * - Save as Draft: the content differs from what was loaded AND from the previous stored version.
 * - Send / Publish: the content differs from the previous stored version; on a draft (which already
 *   differs) they are enabled straight away.
 * - Editing a draft back to exactly the previous version disables everything.
 * @param {object} p
 * @param {string} p.content          what the editor holds now
 * @param {string} p.loadedContent    what the editor opened with (the latest version)
 * @param {boolean} p.isDraft         the latest version is a DRAFT (either source)
 * @param {boolean} p.isHumanDraft    the latest version is a human-edited DRAFT (Save overwrites it)
 * @param {?string} p.previousContent the content under a human-edited draft; null while unknown
 * @returns {{ changed: boolean, canSaveDraft: boolean, canSendOrPublish: boolean }}
 */
export const getEditorButtonRules = ({ content, loadedContent, isDraft, isHumanDraft, previousContent = null }) => {
  const changed = !isSameContent(content, loadedContent);
  // An empty editor is never stored
  const empty = normalizeContent(content) === '';
  // Back to the version under the draft: nothing may be stored (unknown previous content: not refused here,
  // the server still checks)
  const backToPrevious = isHumanDraft && previousContent !== null && isSameContent(content, previousContent);
  return {
    changed,
    canSaveDraft: changed && !empty && !backToPrevious,
    // a draft is sent or published as it is; anything else needs a change first
    canSendOrPublish: (isDraft ? true : changed) && !empty && !backToPrevious,
  };
};

// Cancel asks first only when something changed; otherwise it just closes the editor.
export const shouldConfirmCancel = (content, loadedContent) => !isSameContent(content, loadedContent);

// ── Stale-tab check values (CONC-5) ───────────────────────────────────────────
const toMs = (value) => {
  const raw = value && typeof value === 'object' && '$date' in value ? value.$date : value;
  if (raw === null || raw === undefined || raw === '') return NaN;
  return typeof raw === 'number' ? raw : Date.parse(raw);
};

/**
 * The latest version's sb_unique_id and last_updated_time_stamp to send with a save, send or publish: the ones
 * the server returned for the draft this page saved last when it is still the latest version, otherwise the
 * latest /versions row's. The row's exact stamp (last_updated_time_stamp_ms, to the millisecond, which the
 * server's stale check needs) is used when the row carries it; its last_updated_time_stamp is an HTTP date,
 * exact to the second only (kept as the fallback for a response without the exact one).
 */
export const getStaleCheckValues = (latestRow, lastSaved) => {
  const exactRowStamp = latestRow?.last_updated_time_stamp_ms ?? null;
  const fromRow = {
    latest_sb_unique_id: latestRow?.sb_unique_id ?? null,
    latest_last_updated_time_stamp: exactRowStamp ?? latestRow?.last_updated_time_stamp ?? null,
  };
  if (!lastSaved?.sb_unique_id || lastSaved.sb_unique_id !== latestRow?.sb_unique_id) return fromRow;
  // an exact row stamp is what the page shows of the latest version: always sent as it is
  if (exactRowStamp !== null) return fromRow;
  const savedMs = toMs(lastSaved.last_updated_time_stamp);
  // the row moved on since (another tab saved): send what the page shows, so the server says STALE
  const rowMs = toMs(latestRow?.last_updated_time_stamp);
  if (!Number.isNaN(rowMs) && Math.floor(rowMs / 1000) !== Math.floor(savedMs / 1000)) return fromRow;
  return {
    latest_sb_unique_id: lastSaved.sb_unique_id,
    latest_last_updated_time_stamp: lastSaved.last_updated_time_stamp,
  };
};

// The request body of POST /approval/save_revision for a draft: only the content and the stale values;
// the server takes everything else (owner, version, metadata) from the stored JD.
export const buildDraftSavePayload = ({ jobId, content, staleCheck }) => ({
  save_mode: 'draft',
  job_id: jobId,
  job_desc_md: content,
  generated_job_desc_md: content,
  ...staleCheck,
});

// The values a successful save hands back, kept for the next save's stale check
export const pickSavedDraft = (response) => (response?.sb_unique_id
  ? { sb_unique_id: response.sb_unique_id, last_updated_time_stamp: response.last_updated_time_stamp ?? null }
  : null);

export const getDraftSavedToast = (response) =>
  (response?.draft_action === 'updated' ? DRAFT_UPDATED_TOAST : DRAFT_SAVED_TOAST);

// ── Refusals ──────────────────────────────────────────────────────────────────
export const DRAFT_SAVE_ERRORS = {
  STALE: 'This JD has changed since you opened it. Reload it to see the latest version, then make your changes again.',
  IN_REVIEW: "This JD is in a review cycle, so it can't be saved as a draft.",
  NOT_OWNER: 'Only the owner of this JD can save a draft of it.',
  NO_CHANGES: 'Make changes first: this would save the same content as the version before it.',
  JD_BUSY: 'This JD is being changed by another action. Please try again in a moment.',
  GENERATING: 'This JD is being revised. Wait for the Revise to finish before saving a draft.',
  PUBLISHED: 'This JD is published. Unpublish it before saving a draft.',
  OUT_OF_SCOPE: "This JD is outside your view scope, so you can't save it.",
};
export const DRAFT_SAVE_FAILED_TITLE = 'Draft not saved';
const DRAFT_SAVE_FALLBACK = 'The draft could not be saved. Your changes are still in the editor; please try again.';

// { reason, message, isStale } for a failed save (an axios error), the server's reason first
export const getDraftSaveError = (error) => {
  const data = error?.response?.data || {};
  const reason = data.reason || null;
  return {
    reason,
    isStale: reason === 'STALE',
    message: DRAFT_SAVE_ERRORS[reason] || data.message || DRAFT_SAVE_FALLBACK,
  };
};
